import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  User,
  Calendar,
  RefreshCw,
  LogOut,
  Loader2,
  FileText,
  CalendarDays,
} from "lucide-react";
import { CleanKinLogo } from "@/components/Layout";
import { BookingsTab } from "./dashboard/BookingsTab";
import { RecurringTab } from "./dashboard/RecurringTab";
import { CalendarTab } from "./dashboard/CalendarTab";
import { ProfileTab } from "./dashboard/ProfileTab";
import { InvoicesTab } from "./dashboard/InvoicesTab";
import { SubscriptionsTab } from "./dashboard/SubscriptionsTab";
import { logError, logWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [recurringBookings, setRecurringBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  const customerName = localStorage.getItem("customer_name");
  const isLoggedIn = localStorage.getItem("customer_logged_in") === "true";

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, bookingsRes, recurringRes, servicesRes, invoicesRes, subsRes] =
        await Promise.all([
          axios.get(`${API}/customer/me`),
          axios.get(`${API}/customer/bookings`),
          axios.get(`${API}/customer/recurring`),
          axios.get(`${API}/services`),
          axios.get(`${API}/customer/invoices`),
          axios.get(`${API}/customer/subscriptions`),
        ]);
      setProfile(profileRes.data);
      setBookings(bookingsRes.data);
      setRecurringBookings(recurringRes.data);
      setServices(servicesRes.data);
      setInvoices(invoicesRes.data);
      setSubscriptions(subsRes.data);
    } catch (error) {
      logError("Error fetching customer dashboard data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("customer_logged_in");
        localStorage.removeItem("customer_name");
        localStorage.removeItem("customer_id");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    fetchAllData();
  }, [isLoggedIn, navigate, fetchAllData]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/customer/logout`);
    } catch (err) {
      // Network/server failure during logout is non-fatal —
      // we still clear local UI state so the user is signed out client-side.
      logWarn("Logout request failed; clearing local state anyway.", err);
    }
    localStorage.removeItem("customer_logged_in");
    localStorage.removeItem("customer_name");
    localStorage.removeItem("customer_id");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2D5016] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      <header className="bg-white border-b border-[#E2E8E0] sticky top-0 z-50">
        <div className="container-clean">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <CleanKinLogo size="small" />
            </Link>
            <div className="flex items-center gap-4">
              <span className="text-[#5C6B57] hidden sm:block">
                Welcome, {customerName}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-[#5C6B57]"
                data-testid="customer-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container-clean py-8">
        <div className="mb-8">
          <h1
            className="text-3xl font-bold text-[#1A2E12]"
            data-testid="customer-dashboard-title"
          >
            My Account
          </h1>
          <p className="text-[#5C6B57] mt-1">
            Manage your bookings and schedule recurring cleans
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Booking History
            </TabsTrigger>
            <TabsTrigger value="recurring" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Recurring Cleans
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex items-center gap-2" data-testid="tab-subscriptions">
              <RefreshCw className="w-4 h-4" />
              Subscriptions
            </TabsTrigger>
            <TabsTrigger
              value="calendar"
              className="flex items-center gap-2"
              data-testid="calendar-tab"
            >
              <CalendarDays className="w-4 h-4" />
              Calendar View
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2" data-testid="tab-invoices">
              <FileText className="w-4 h-4" />
              Invoices
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <BookingsTab bookings={bookings} />
          </TabsContent>

          <TabsContent value="recurring">
            <RecurringTab
              recurringBookings={recurringBookings}
              services={services}
              profile={profile}
              onRefresh={fetchAllData}
            />
          </TabsContent>

          <TabsContent value="subscriptions">
            <SubscriptionsTab
              subscriptions={subscriptions}
              onChanged={fetchAllData}
            />
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarTab
              recurringBookings={recurringBookings}
              onSwitchToRecurring={() => setActiveTab("recurring")}
            />
          </TabsContent>

          <TabsContent value="profile">
            <ProfileTab profile={profile} />
          </TabsContent>

          <TabsContent value="invoices">
            <InvoicesTab invoices={invoices} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CustomerDashboard;
